import type { Feature, FeatureCollection, Geometry, Polygon, Position } from 'geojson';
import type {
	Barrier,
	CountermeasureApplication,
	Corridor,
	Curve,
	Program,
	Project,
	Sign
} from '#data/safesystem/safecurves/contract.js';
import { isChevron } from './sheeting';

// One project's rows, as read from the local DB for the main SafeCurves map.
export interface ProjectRows {
	program: Program;
	project: Project;
	corridors: Corridor[];
	curves: Curve[];
	signs: Sign[];
	signApps: CountermeasureApplication[];
	barriers: Barrier[];
	barrierApps: CountermeasureApplication[];
}

export interface ProgramsMapFeatures {
	projects: FeatureCollection;
	signs: FeatureCollection;
	barriers: FeatureCollection;
}

const collection = (features: Feature[]): FeatureCollection => ({
	type: 'FeatureCollection',
	features
});

// A geometry's line parts; anything that isn't a line has none.
export const lineParts = (g: Geometry): Position[][] =>
	g.type === 'LineString' ? [g.coordinates] : g.type === 'MultiLineString' ? g.coordinates : [];

// Chevrons stand on the outside of the bend, so their outline is a band this far (m) out from the curve line.
const CHEVRON_BAND_M = { inner: 4, outer: 14 };
const M_PER_DEG = 111_320;

// Lng/lat to local metres (equirectangular about the first point) and back; plenty for one curve.
function localFrame(coords: Position[]) {
	const kx = M_PER_DEG * Math.cos(((coords[0]?.[1] ?? 0) * Math.PI) / 180);
	return {
		pts: coords.map(([x, y]) => [x * kx, y * M_PER_DEG]),
		toLngLat: ([x, y]: number[]): Position => [x / kx, y / M_PER_DEG]
	};
}

// The line shifted `metres` towards the outside of its bend (an arc's midpoint bulges outward from its chord).
function offsetOutside(coords: Position[], metres: number): Position[] {
	const { pts, toLngLat } = localFrame(coords);
	const [first, last] = [pts[0], pts[pts.length - 1]];
	const mid = pts[Math.floor(pts.length / 2)];
	const cross =
		(last[0] - first[0]) * (mid[1] - first[1]) - (last[1] - first[1]) * (mid[0] - first[0]);
	const d = cross < 0 ? -metres : metres; // positive = left of the digitised direction
	return pts.map((p, i) => {
		const a = pts[Math.max(i - 1, 0)];
		const b = pts[Math.min(i + 1, pts.length - 1)];
		const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
		return toLngLat([p[0] - ((b[1] - a[1]) / len) * d, p[1] + ((b[0] - a[0]) / len) * d]);
	});
}

// A band between `inner` and `outer` metres off the line, on the outside of its bend.
export function outsideBand(coords: Position[], inner: number, outer: number): Polygon {
	const near = offsetOutside(coords, inner);
	const far = offsetOutside(coords, outer).reverse();
	return { type: 'Polygon', coordinates: [[...near, ...far, near[0]]] };
}

// The point `fraction` (0–1) of the way along the line's length.
function pointAlong(coords: Position[], fraction: number): Position {
	const { pts, toLngLat } = localFrame(coords);
	const steps = pts.slice(1).map((p, i) => Math.hypot(p[0] - pts[i][0], p[1] - pts[i][1]));
	let remaining = steps.reduce((sum, d) => sum + d, 0) * fraction;
	for (const [i, d] of steps.entries()) {
		if (remaining <= d) {
			const t = d === 0 ? 0 : remaining / d;
			return toLngLat([0, 1].map((k) => pts[i][k] + (pts[i + 1][k] - pts[i][k]) * t));
		}
		remaining -= d;
	}
	return coords[coords.length - 1];
}

// `count` posts spaced evenly along the middle of the outside-of-bend band, each centred in an equal share of it.
function chevronPosts(coords: Position[], count: number): Position[] {
	const centreLine = offsetOutside(coords, (CHEVRON_BAND_M.inner + CHEVRON_BAND_M.outer) / 2);
	const n = Math.max(1, Math.round(count));
	return Array.from({ length: n }, (_, i) => pointAlong(centreLine, (i + 0.5) / n));
}

const existing = (a: CountermeasureApplication | undefined) =>
	a != null && !a.exclude && a.existingUnit > 0;

// Existing signs (with their plaque value only where the plaque is existing too); ids are per project.
function signFeatures(
	r: ProjectRows,
	shared: Record<string, string>,
	curveById: Map<string, Curve>
): Feature[] {
	const out: Feature[] = [];
	const appsBySign = Map.groupBy(
		r.signApps.filter((a) => a.signId),
		(a) => `${a.signId}:${a.signPart}`
	);
	for (const s of r.signs) {
		const sign = appsBySign.get(`${s.id}:sign`)?.[0];
		if (!existing(sign)) continue;
		const id = `${r.project.id}:${s.id}`;
		const props = {
			id,
			...shared,
			advisory_sign: s.advisorySign ?? '',
			advisory_sign_direction: s.advisorySignDirection ?? '',
			road_name: curveById.get(s.curveId)?.roadName ?? ''
		};
		// A chevron run holds one application (its existing count) on its first post: an icon per existing post, plus a notional outline sharing their id, so a click on either selects (and highlights) the run once.
		const curveLine = isChevron(s)
			? lineParts(curveById.get(s.curveId)?.geom ?? s.geom).flat()
			: [];
		if (curveLine.length > 1) {
			const runProps = { ...props, action: 'Chevrons', existing_chevrons: sign?.existingUnit };
			out.push({
				type: 'Feature',
				id,
				geometry: {
					type: 'MultiPoint',
					coordinates: chevronPosts(curveLine, sign?.existingUnit ?? 1)
				},
				properties: runProps
			});
			out.push({
				type: 'Feature',
				id,
				geometry: outsideBand(curveLine, CHEVRON_BAND_M.inner, CHEVRON_BAND_M.outer),
				properties: runProps
			});
			continue;
		}
		out.push({
			type: 'Feature',
			id,
			geometry: s.geom,
			properties: {
				...props,
				action: sign?.existingStatus === 'retain' ? 'Retain' : 'Replace',
				// Null hides the plaque layer's icon (its style filters on a value).
				advisory_plaque_value: existing(appsBySign.get(`${s.id}:plaque`)?.[0])
					? s.advisoryPlaqueValue
					: null
			}
		});
	}
	return out;
}

// Ids are per project, since a sign or barrier can sit in several projects with different answers.
// Literals, not `STATUS_LABELS`, as the projects layer style matches on them; a project needing nothing counts as assessed.
const mapStatus = (p: Project) =>
	p.completionDate
		? 'Finalized'
		: p.assessment === 'assessed' || p.assessment === 'notRequired'
			? 'Assessed'
			: 'Open';

export function buildProgramsMapFeatures(rows: ProjectRows[]): ProgramsMapFeatures {
	const projects: Feature[] = [];
	const signs: Feature[] = [];
	const barriers: Feature[] = [];
	for (const r of rows) {
		const shared = {
			project_id: r.project.id,
			project_name: r.project.name,
			program_id: r.program.id,
			program_name: r.program.name
		};
		const curveById = new Map(r.curves.map((c) => [c.id, c]));
		const roadOf = (curveId: string) => curveById.get(curveId)?.roadName ?? '';

		if (r.corridors.length > 0)
			projects.push({
				type: 'Feature',
				id: r.project.id,
				geometry: {
					type: 'MultiLineString',
					coordinates: r.corridors.flatMap((c) => lineParts(c.geom))
				},
				properties: {
					id: r.project.id,
					...shared,
					status: mapStatus(r.project),
					completion_date: r.project.completionDate ?? '',
					road_name: [...new Set(r.corridors.map((c) => c.roadName).filter(Boolean))].join(', '),
					county: [...new Set(r.corridors.map((c) => c.county))].join(', '),
					corridor_count: r.corridors.length,
					length_km: r.project.lengthKm,
					estimated_cost: r.project.estimatedCost
				}
			});

		signs.push(...signFeatures(r, shared, curveById));

		const inPlace = new Map(
			r.barrierApps.filter((a) => a.existingStatus === 'retain').map((a) => [a.barrierId, a])
		);
		for (const b of r.barriers) {
			const a = inPlace.get(b.id);
			if (!a) continue;
			const id = `${r.project.id}:${b.id}`;
			barriers.push({
				type: 'Feature',
				id,
				geometry: b.geom,
				properties: {
					id,
					...shared,
					side: b.side,
					// Draws the barrier on its curve's side of the road, as the Programs curve map does.
					visual_offset: curveById.get(b.curveId)?.visualOffset ?? '',
					clearzone_grade: b.clearzoneGrade,
					length_km: a.existingUnit,
					road_name: roadOf(b.curveId)
				}
			});
		}
	}
	return {
		projects: collection(projects),
		signs: collection(signs),
		barriers: collection(barriers)
	};
}
